import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@components/ui/accordion';
import {
  SettingsContainer,
  SettingsContent,
  SettingsHeaderContainer
} from '@components/ui/settings';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import { useWorkflow } from '@hooks/useWorkflow';
import { CircularProgress } from '@mui/joy';
import { LogViewer } from '@patternfly/react-log-viewer';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LoaderBox } from '@/components/ui/loader';
import { ParamsList } from '@/routes/routes.types';

import AnalyticsCard, { podStatusToProgress } from './AnalyticsCard';

export const POLL_INTERVAL = 5000;

const AnalyticsDetails = () => {
  const process = useProcess();
  const { workflow } = useWorkflow();
  const { analyticsStore } = useStores();
  const analyticsId = useParams<string>()[ParamsList.AnalyticsId];

  const analytics = analyticsStore.get(analyticsId);

  const formatLogsForDisplay = (logs?: string) => {
    if (!logs || typeof logs !== 'string') {
      return [];
    }

    try {
      const parsedLogs = JSON.parse(logs) as Record<string, unknown> | string;

      if (typeof parsedLogs !== 'object') {
        return parsedLogs;
      }

      if (parsedLogs.stack) {
        return parsedLogs.stack as string;
      }

      return JSON.stringify(parsedLogs, null, 2);
    } catch (_error) {
      return logs;
    }
  };

  useEffect(() => {
    if (!(analytics?.shouldBePolled && workflow?.published_id)) {
      return;
    }
    analyticsStore.startSinglePolling(analytics, workflow);
    return () => analyticsStore.stopPolling(analytics.id);
  }, [analytics, workflow, analyticsStore]);

  if (!analyticsId) {
    return <div>Analytics ID not found</div>;
  }

  if (!analytics) {
    if (!workflow?.published_id) {
      return;
    }

    void analyticsStore
      .fetchAndParseAnalytics(analyticsId, workflow?.published_id)
      .then(parsedAnalytics => {
        if (!parsedAnalytics) {
          return;
        }
        return analyticsStore.loadEtlJob(parsedAnalytics);
      });
  }

  if (!process || process.isSmallProcess) {
    return (
      <LoaderBox>
        <CircularProgress />
      </LoaderBox>
    );
  }

  if (!analytics) {
    return <div className="container m-2">Analytics not found</div>;
  }
  return (
    <SettingsContainer>
      <SettingsHeaderContainer>
        <AnalyticsCard analytics={analytics} />
      </SettingsHeaderContainer>
      <SettingsContent>
        <h1 className="mb-6 font-medium">Analytics details</h1>

        <Accordion collapsible type="single">
          <AccordionItem
            className="rounded-md border border-zinc-300 border-solid bg-white bg-clip-border font-normal text-sm shadow-md"
            value="item-1"
          >
            <AccordionTrigger className="px-4">
              <div className="flex items-center">
                <div className="mr-2">Logs</div>
                {podStatusToProgress(analytics.jobExecutions?.[0]?.status)}
              </div>
            </AccordionTrigger>
            <AccordionContent className="h-72 rounded-b-md border-zinc-300 border-t border-solid bg-zinc-50 pl-1 font-mono">
              <LogViewer
                data={formatLogsForDisplay(
                  analytics.jobExecutions?.[0]?.logs ?? ''
                )}
                hasLineNumbers={true}
                height="100%"
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </SettingsContent>
    </SettingsContainer>
  );
};

export default observer(AnalyticsDetails);
